<template>
  <div v-if="isOwner || publiclyVisible">
    <Breadcrumb>
      <ol itemscope itemtype="https://schema.org/BreadcrumbList">
        <li class="breadcrumb-item" itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
          <a href="/visual-schedules">Visual Schedules</a>
        </li>
        <li
          class="breadcrumb-item active"
          aria-current="page"
          itemprop="itemListElement"
          itemscope
          itemtype="https://schema.org/ListItem"
        >
          {{ visualSchedule.title }}
        </li>
      </ol>
      <template slot="actions" v-if="isOwner">
        <b-button size="sm" variant="primary" class="mr-2" @click="handleAddCard"
          ><b-icon icon="plus-lg"></b-icon> Add cards</b-button
        >
        <b-button
          v-if="$store.state.userId && !pubVisOrSubmitted"
          size="sm"
          variant="secondary"
          class="mr-2"
          @click="handleSubmitSchedule"
          ><b-icon icon="globe2"></b-icon> Make public</b-button
        >
        <b-button size="sm" variant="secondary" class="mr-2" @click="handleAddPatient"
          ><b-icon icon="person"></b-icon> Patients
          {{ visualSchedule.patients.length ? `(${visualSchedule.patients.length})` : '' }}</b-button
        >
        <VisualScheduleSettings />
      </template>
    </Breadcrumb>

    <b-card header-tag="header">
      <template #header>
        <h2 class="m-0"><b-icon icon="view-list"></b-icon> {{ visualSchedule.title }}</h2>
      </template>

      <VisualScheduleViewer :previewMode="!can.manipulateCards" />

      <b-modal v-if="isOwner" v-model="addCardModal" title="Add cards to the schedule" hide-footer modal-tall size="lg">
        <CardAdder @close-modal="closeCardModalHAndler()" />
      </b-modal>

      <b-modal
        v-if="isOwner"
        v-model="patientModal"
        title="Manage patients using this schedule"
        no-close-on-backdrop
        hide-footer
      >
        <PatientManager @close-modal="patientModal = false" />
      </b-modal>

      <DeviseAuth v-if="isOwner" size="lg" :show="authModal" @close-modal="authModal = false">
        <template v-slot:mobile-image>
          <b-img :src="imageMobile" fluid class="d-lg-none w-100"></b-img>
        </template>
        <template v-slot:image>
          <b-img :src="image" fluid class="d-none d-lg-block h-100"></b-img>
        </template>
      </DeviseAuth>
    </b-card>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import axios from 'axios';
import { EventBus } from './event-bus';
import Breadcrumb from '../components/Breadcrumb.vue';
import CardAdder from './components/CardAdder.vue';
import DeviseAuth from '../components/DeviseAuth.vue';
import PatientManager from './components/PatientManager.vue';
import VisualScheduleViewer from './components/VisualScheduleViewer.vue';
import VisualScheduleSettings from './components/VisualScheduleSettings.vue';
import image from '../images/visual-schedule-login.jpg';
import imageMobile from '../images/visual-schedule-login-mobile.jpg';

export default {
  components: {
    CardAdder,
    Breadcrumb,
    DeviseAuth,
    PatientManager,
    VisualScheduleViewer,
    VisualScheduleSettings,
  },

  props: {
    visualSchedule: {
      type: Object,
      required: true,
    },
    abilities: {
      type: Object,
      required: true,
    },
    defaultCards: {
      type: Array,
    },
    directUploadUrl: {
      type: String,
    },
    isOwner: {
      type: Boolean,
      default: false,
    },
    publiclyVisible: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: String,
    },
    cardTypes: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      addCardModal: false,
      authModal: false,
      patientModal: false,
      image,
      imageMobile,
      pubVisOrSubmitted: this.visualSchedule.submitted_for_approval,
    };
  },

  mounted() {
    EventBus.$on('show-cta', this.showAuthModal);
    this.fetchVisualScheduleOptions();
    this.setVisualSchedule(this.visualSchedule);
    this.setAbilities(this.abilities);
    this.setDefaultCards(this.defaultCards);
    this.setCardTypes(this.cardTypes);
    this.setUserId(this.userId);
    this.setDirectUploadUrl(this.directUploadUrl);
  },

  destroyed() {
    EventBus.$off('show-cta', this.showAuthModal);
  },

  methods: {
    ...mapMutations([
      'setAbilities',
      'setVisualSchedule',
      'setDefaultCards',
      'setDefaultImages',
      'setUserId',
      'setCardTypes',
      'setDirectUploadUrl',
    ]),
    ...mapActions(['fetchVisualScheduleOptions']),

    closeCardModalHAndler() {
      this.addCardModal = false;
      EventBus.$emit('update-schedule');
    },

    handleAddCard() {
      if (this.can.manipulateCards) {
        this.addCardModal = true;
      } else this.showUpgradeMessage('add more cards');
    },

    handleSubmitSchedule() {
      axios.post(`/api/v2/visual-schedules/${this.visualSchedule.id}/submit-for-approval`).then((response) => {
        if (response.status === 200) {
          this.pubVisOrSubmitted = true;
          this.$toast.open({
            type: 'success',
            message: "Ambiki will review your request and let you know when it's public!",
          });
        }
      });
    },

    handleAddPatient() {
      if (this.can.associatePatients) {
        this.patientModal = true;
      } else this.showUpgradeMessage('save for specific patients');
    },

    showAuthModal() {
      this.authModal = true;
    },

    showUpgradeMessage(message) {
      this.$toast.info(`You need to have an active Therapy Tools add-on to ${message}`, {
        duration: 10000,
        dismissible: true,
      });
    },
  },

  computed: {
    ...mapState(['can']),
    cards() {
      return this.visualSchedule.visual_schedule_cards_attributes;
    },
  },
};
</script>
